import React, { useState } from 'react'
import {
	Box,
	Button,
	ButtonBase,
	Chip,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	Tooltip,
	Modal
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ImageIcon from '@mui/icons-material/Image'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { mainBlue, mainGreen, mainRed } from '../../../utils/color'
import {
	expenseIsPendingApproval,
	expenseTypeLabel,
	formatMoney,
	orderCode,
	resolveImageUrl,
	sectionSx,
} from '../cashflowUtils'
import { EmptyRow, MoneyStat } from './CashflowCommon'
import ExpenseDialog from './ExpenseDialog'
import ExpenseDetailDialog, { emptyExpenseDetail } from './ExpenseDetailDialog'

// Mỗi loại chi một bảng riêng, tiêu đề bảng đã nói rõ loại nên bỏ cột "Loại".
// Mỗi loại mang một tông riêng với viền trái đậm, giống thẻ tài khoản ở tab Báo cáo ngày,
// để cuộn giữa ba bảng vẫn biết đang đứng ở loại chi nào.
const EXPENSE_GROUPS = [
	{ type: 'shipping', title: 'Chi ship', accent: mainBlue, background: '#E8F1FB', totalColor: mainRed },
	{ type: 'other', title: 'Chi khác', accent: '#B26A00', background: '#FBF2E3', totalColor: mainRed },
	{ type: 'cashout', title: 'Rút tiền', accent: '#9C1C4A', background: '#FBECF1', totalColor: '#9C1C4A' },
]

const sumAmount = (items) => items.reduce((total, item) => total + (Number(item.amount) || 0), 0)

// Khoản chờ xác nhận chưa được tính vào báo cáo ngày, tách ra để tổng ở đây khớp với báo cáo
const countedTotal = (items) => sumAmount(items.filter((item) => !expenseIsPendingApproval(item)))

const pendingTotal = (items) => sumAmount(items.filter(expenseIsPendingApproval))

export default function ExpensesTab({
	bankAccounts,
	canApproveExpense,
	canManageExpense,
	cashoutExpenseCategories,
	closeExpenseDialog,
	confirmExpense,
	deleteExpense,
	editingExpenseId,
	expenseDialogOpen,
	expenseForm,
	expenseOrderOptions,
	getLockedOrderExpense,
	openCreateExpense,
	otherExpenseCategories,
	saveExpense,
	selectedExpenseCategory,
	selectedExpenseOrders,
	selectedShippingIncome,
	setImageViewer,
	setExpenseForm,
	shippingExpenseCategories,
	startEditExpense,
	summary,
}) {
	const [expenseDetail, setExpenseDetail] = useState(emptyExpenseDetail)
	const [selectedImage, setSelectedImage] = useState(null);
	const expenseItems = summary?.expenses?.items || []
	const pendingApprovalCount = summary?.expenses?.pendingApprovalItems?.length || 0
	const approvalStatusLabel = (expense) => {
		if (expenseIsPendingApproval(expense)) return 'Chờ xác nhận'
		if (expense.approvalRequired) return 'Đã xác nhận'
		return 'Không cần xác nhận'
	}
	const expenseGroups = EXPENSE_GROUPS.map((group) => ({
		...group,
		items: expenseItems.filter((expense) => expense.type === group.type),
	}))

	const renderExpenseRow = (expense) => {
		const photoUrls = Array.isArray(expense.photos) ? expense.photos.filter(Boolean) : []
		const orders = expense.orders || []
		const hasDetail = photoUrls.length > 0 || orders.length > 0
		// Dùng span để khối nội dung vẫn hợp lệ khi bọc trong nút xem chi tiết
		const contentBlock = (
			<>
				<Typography component='span' sx={{ display: 'block', fontWeight: 800 }}>
					{expense.content}
					{hasDetail && (
						<VisibilityOutlinedIcon sx={{ fontSize: 15, color: '#637381', ml: 0.5, verticalAlign: 'middle' }} />
					)}
				</Typography>
				{orders.length > 0 && (
					<Typography component='span' sx={{ display: 'block', fontSize: 12, color: '#637381' }}>
						{orders.length} đơn: {orders.map((order) => orderCode(order)).join(', ')}
					</Typography>
				)}
			</>
		)
		return (
			<TableRow key={expense._id} selected={editingExpenseId === expense._id}>
				<TableCell>
					{hasDetail ? (
						<Tooltip title='Xem ảnh và đơn liên quan'>
							<ButtonBase
								aria-label={`Xem ảnh và đơn liên quan của ${expense.content || 'khoản chi'}`}
								onClick={() =>
									setExpenseDetail({
										open: true,
										title: expense.content || 'Chi tiết khoản chi',
										photos: photoUrls,
										orders,
									})
								}
								sx={{
									display: 'block',
									textAlign: 'left',
									width: '100%',
									borderRadius: '8px',
									p: 0.5,
									mx: -0.5,
									'&:hover': { background: '#EEF6F0' },
								}}>
								{contentBlock}
							</ButtonBase>
						</Tooltip>
					) : (
						contentBlock
					)}
					{photoUrls.length > 0 && (
						<Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap alignItems='center' sx={{ mt: 0.75 }}>
							<Chip
								size='small'
								icon={<ImageIcon />}
								label={`${photoUrls.length} ảnh đính kèm`}
								sx={{ height: 26, fontWeight: 800, borderRadius: '8px' }}
							/>
							{photoUrls.map((photo, photoIndex) => {
								const photoUrl = resolveImageUrl(photo)
								const title = `${expense.content || 'Khoản chi'} - ảnh ${photoIndex + 1}`
								return (
									<Tooltip key={`${expense._id}-photo-${photoIndex}`} title='Xem ảnh đính kèm'>
										<ButtonBase
											aria-label={title}
											onClick={(event) => {
												event.stopPropagation()
												setImageViewer?.({ open: true, url: photoUrl, title })
											}}
											sx={{
												width: 44,
												height: 44,
												borderRadius: '8px',
												overflow: 'hidden',
												border: '1px solid #DDE7DF',
												background: '#F8FBF9',
												flex: '0 0 auto',
											}}>
											<Box
												component='img'
												loading='lazy'
												src={photoUrl}
												alt={title}
												sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
											/>
										</ButtonBase>
									</Tooltip>
								)
							})}
						</Stack>
					)}
				</TableCell>
				<TableCell>{expense.category?.name || expenseTypeLabel(expense.type)}</TableCell>
				<TableCell>
					{expense.source === 'cash' ? 'Tiền mặt mặc định' : expense.bankAccount?.name || 'Ngân hàng chưa gán'}
				</TableCell>
				<TableCell>
					<Chip
						size='small'
						color={expenseIsPendingApproval(expense) ? 'warning' : 'success'}
						label={approvalStatusLabel(expense)}
					/>
				</TableCell>
				<TableCell align='right' sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
					{formatMoney(expense.amount)}
				</TableCell>
				<TableCell align='right'>
					{(canManageExpense || canApproveExpense) && (
						<Stack direction='row' spacing={0.5} justifyContent='flex-end'>
							{canApproveExpense && expenseIsPendingApproval(expense) && (
								<Button
									size='small'
									variant='contained'
									startIcon={<CheckIcon />}
									sx={{ background: mainGreen, whiteSpace: 'nowrap' }}
									onClick={() => confirmExpense(expense._id)}>
									Xác nhận
								</Button>
							)}
							{canManageExpense && (
								<>
									<IconButton
										aria-label='Sửa khoản chi'
										color='primary'
										size='small'
										onClick={() => startEditExpense(expense)}>
										<EditIcon fontSize='small' />
									</IconButton>
									<IconButton
										aria-label='Xoá khoản chi'
										color='error'
										size='small'
										onClick={() => deleteExpense(expense._id)}>
										<DeleteIcon fontSize='small' />
									</IconButton>
								</>
							)}
						</Stack>
					)}
				</TableCell>
			</TableRow>
		)
	}
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
			<ExpenseDetailDialog
				expenseDetail={expenseDetail}
				setExpenseDetail={setExpenseDetail}
				setImageViewer={setImageViewer}
			/>
			<ExpenseDialog
				bankAccounts={bankAccounts}
				canManageExpense={canManageExpense}
				cashoutExpenseCategories={cashoutExpenseCategories}
				closeExpenseDialog={closeExpenseDialog}
				editingExpenseId={editingExpenseId}
				expenseDialogOpen={expenseDialogOpen}
				expenseForm={expenseForm}
				expenseOrderOptions={expenseOrderOptions}
				getLockedOrderExpense={getLockedOrderExpense}
				otherExpenseCategories={otherExpenseCategories}
				saveExpense={saveExpense}
				selectedExpenseCategory={selectedExpenseCategory}
				selectedExpenseOrders={selectedExpenseOrders}
				selectedShippingIncome={selectedShippingIncome}
				setExpenseForm={setExpenseForm}
				shippingExpenseCategories={shippingExpenseCategories}
			/>
			<Paper elevation={0} sx={sectionSx}>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					spacing={1}
					alignItems={{ xs: 'stretch', md: 'center' }}
					justifyContent='space-between'
					sx={{ mb: 2 }}>
					<Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap>
						<Typography sx={{ fontWeight: 800 }}>Chi phí trong ngày</Typography>
						<Typography sx={{ fontWeight: 900, color: mainRed, fontVariantNumeric: 'tabular-nums' }}>
							{formatMoney(countedTotal(expenseItems))}
						</Typography>
					</Stack>
					{pendingApprovalCount > 0 && (
						<Chip size='small' color='warning' label={`${pendingApprovalCount} chờ xác nhận`} />
					)}
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						disabled={!canManageExpense}
						sx={{ background: mainGreen }}
						onClick={openCreateExpense}>
						Tạo khoản chi
					</Button>
				</Stack>
				<Stack className='cashflow-section-bleed' spacing={2.5}>
					{expenseGroups.map((group) => (
						<Paper
							key={group.type}
							elevation={0}
							sx={{
								border: '1px solid #DCE8DF',
								borderLeft: `5px solid ${group.accent}`,
								borderRadius: '12px',
								background: '#fff',
								overflow: 'hidden',
							}}>
							<Stack
								direction='row'
								alignItems='center'
								justifyContent='space-between'
								flexWrap='wrap'
								useFlexGap
								spacing={1}
								sx={{ px: 1.5, py: 1.25, background: group.background, borderBottom: '1px solid #DCE8DF' }}>
								<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
									<Typography sx={{ fontWeight: 900, color: group.accent }}>{group.title}</Typography>
									<Chip size='small' label={`${group.items.length} khoản`} sx={{ height: 22, background: '#fff' }} />
									{pendingTotal(group.items) > 0 && (
										<Chip
											size='small'
											color='warning'
											label={`Chờ xác nhận ${formatMoney(pendingTotal(group.items))}`}
											sx={{ height: 22 }}
										/>
									)}
								</Stack>
								<Typography sx={{ fontWeight: 900, color: group.totalColor, fontVariantNumeric: 'tabular-nums' }}>
									{formatMoney(countedTotal(group.items))}
								</Typography>
							</Stack>
							<Box className='cashflow-scroll cashflow-table-grid' sx={{ overflowX: 'auto' }}>
								<Table size='small' sx={{ minWidth: 920 }}>
									<TableHead>
										<TableRow>
											<TableCell>Nội dung</TableCell>
											<TableCell>Khoản chi</TableCell>
											<TableCell>Tài khoản</TableCell>
											<TableCell>Trạng thái</TableCell>
											<TableCell align='right'>Số tiền</TableCell>
											<TableCell align='right'></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{group.items.length === 0 && (
											<EmptyRow colSpan={6} text={`Chưa có khoản ${group.title.toLowerCase()}`} />
										)}
										{group.items.map(renderExpenseRow)}
									</TableBody>
								</Table>
							</Box>
						</Paper>
					))}
				</Stack>
			</Paper>
			<Paper elevation={0} sx={sectionSx}>
				<Typography sx={{ fontWeight: 800, mb: 2 }}>Thu ship và chênh lệch</Typography>
				<Box
					className='cashflow-section-bleed'
					sx={{
						display: 'grid',
						gap: 2,
						px: { xs: 0, sm: 0 },
						gridTemplateColumns: {
							xs: '1fr',
							sm: 'repeat(3, minmax(0, 1fr))',
						},
					}}>
					<MoneyStat compact label='Thu ship' value={summary?.income?.totalShippingIncome} color={mainGreen} />
					<MoneyStat compact label='Chi ship' value={summary?.expenses?.totalShippingExpense} color={mainRed} />
					<MoneyStat
						compact
						label='Chênh lệch'
						value={summary?.shippingDifference}
						color={(summary?.shippingDifference || 0) < 0 ? mainRed : mainGreen}
					/>
				</Box>
				<Box className='cashflow-table-shell cashflow-scroll cashflow-table-grid' sx={{ mt: 2 }}>
					<Table size='small' sx={{ minWidth: 640 }}>
						<TableHead>
							<TableRow>
								<TableCell>Đơn</TableCell>
								<TableCell>Ảnh đính kèm</TableCell>
								<TableCell>Thanh toán</TableCell>
								<TableCell align='right'>Phí ship thu</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{summary?.orders?.shippingOrders?.length === 0 && <EmptyRow colSpan={3} text='Chưa có đơn thu ship' />}
							{summary?.orders?.shippingOrders?.map((order) => (
								<TableRow key={order._id}>
									<TableCell sx={{ fontWeight: 800 }}>{orderCode(order)}</TableCell>
									<TableCell>
										{order.photoAttach?.length > 0 ? (
											<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
												{order.photoAttach.map((photo, index) => (
													<Box
														key={index}
														component="img"
														src={photo}
														alt={`Ảnh ${index + 1}`}
														onClick={() => setSelectedImage(photo)}
														sx={{
															width: 60,
															height: 60,
															objectFit: "cover",
															borderRadius: 1,
															cursor: "pointer",
															border: "1px solid #ddd",
															"&:hover": {
																opacity: 0.8,
															},
														}}
													/>
												))}
											</Box>
										) : (
											"-"
										)}
									</TableCell>
									<TableCell>{order.payment === 'transfer' ? 'Chuyển khoản' : 'Tiền mặt'}</TableCell>
									<TableCell align='right' sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
										{formatMoney(order.shippingTotal)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Paper>
			<Modal
				open={Boolean(selectedImage)}
				onClose={() => setSelectedImage(null)}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						outline: "none",
						maxWidth: "90vw",
						maxHeight: "90vh",
					}}
				>
					<Box
						component="img"
						src={selectedImage}
						alt="Preview"
						sx={{
							maxWidth: "90vw",
							maxHeight: "90vh",
							objectFit: "contain",
							borderRadius: 1,
						}}
					/>
				</Box>
			</Modal>
		</Box>
	)
}
